import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import { theme } from "src/Theme/index";
import { useSelector } from "react-redux";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import ProgressBar from "src/Components/ProgressBars/ProgressBar";

function TopSkus({
  filter,
  hier,
  updatedBrand,
  updateSku,
  setSummarized,
  setFull,
}) {
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  // Redux: Get project ID
  const projectId = useSelector((state) => state.userProject.projectId);
  const currentChartType = useSelector((state) => state.chart.currentChartType);

  // Local State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Styled Table Components
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "1px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "1px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: 15,
  }));

  // Fetch API Data
  useEffect(() => {
    async function fetchTopProducts() {
      if (!projectId) {
        console.warn("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Extract hierarchy and hierarchy_value dynamically
      const [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
        "brand",
        "AFIA",
      ];
      try {
        const url = `${getUrl()}/market_overview/top_products`;
        const token = localStorage.getItem("userId") || "";
        const role = localStorage.getItem("role");
        const effectiveHierarchy =
          role === "free_trial" && hier === "sku_db" ? "sku_id" : hier;
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        };
        const payload = {
          project_id: projectId,
          hierarchy: effectiveHierarchy,
          opps_filter: true,
          filters: filter,
          top_5: true,
          time_period: "P12M",
          target: 0,
          token,
        };

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        console.log(result);
        if (!result || Object.keys(result).length === 0) {
          throw new Error("Invalid API response structure.");
        }

        // Transform API response into a usable format
        const formattedData = result["index"].map((name, index) => ({
          name,
          volumeSalesY22: result["Volume_Sales_Previous"]?.[index] || 0,
          volumeSalesY23: result["Volume_Sales_Current"]?.[index] || 0,
          valueSalesY22: result["Value_Sales_Previous"]?.[index] || 0,
          valueSalesY23: result["Value_Sales_Current"]?.[index] || 0,
          dya: (result["DYA"]?.[index]/1_000_000).toFixed(2) || 0,
          iya: result["IYA"]?.[index] || 0,
          growthIdx: result["Growth_IDX"]?.[index] || 0,
          priceIdx: result["Price_Index_YA"]?.[index] || 0,
          impactOnSales: (result["Impact_on_Sales"]?.[index]/1_000_000) .toFixed(2)|| 0,
          fairValueShare: (result["Fair_Value_Sales"]?.[index]/1_000_000).toFixed(2) || 0,
          valueShareIndex: result["Value_Share_Index"]?.[index] || 0,
          volumeShareIndex: result["Volume_Share_Index"]?.[index] || 0
        }));
        setSummarized(result["insights_summary"]);
        setFull(result["insights_full"]);
        setData(formattedData);
      } catch (error) {
        console.error("Error fetching top products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTopProducts();
  }, [projectId, updatedBrand]);

  return (
    <Paper>
      <Typography
        fontWeight={theme.typography.fontWeightBold}
        p={1}
        textAlign={"center"}
      >
        Top SKUs
      </Typography>

      {loading ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <TableContainer sx={{ height: 200 }}>
          <Table aria-label="customized table">
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "#ffffff",
                zIndex: 10,
              }}
            >
              <TableRow>
                <StyledTableCell>#</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Value Share IYA</StyledTableCell>
                <StyledTableCell>Volume Share IYA</StyledTableCell>
                <StyledTableCell>Impact on Sales(M SAR)</StyledTableCell>
                <StyledTableCell>Fair Value Sales (M SAR)</StyledTableCell>
                <StyledTableCell>Growth IDX</StyledTableCell>
                <StyledTableCell>Price IDX</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, idx) => (
                <StyledTableRow key={idx}>
                  <StyledTableCell>
                    {idx === 0 ? <></> : <>{idx}</>}
                  </StyledTableCell>
                  <StyledTableCell>
                    {item.name === "Total Market" ? (
                      <>{updatedBrand}</>
                    ) : (
                      <>{item.name}</>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                        <ColoredBox
                          number={item.valueShareIndex}
                          Color={
                            item.valueShareIndex > 0
                              ? "#0BB783"
                              : item.valueShareIndex === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ColoredBox
                          number={item.volumeShareIndex}
                          Color={
                            item.volumeShareIndex > 0
                              ? "#0BB783"
                              : item.volumeShareIndex === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ColoredBox
                          number={item.impactOnSales}
                          Color={
                            item.impactOnSales > 1
                              ? "#0BB783"
                              : item.impactOnSales === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ColoredBox
                          number={item.fairValueShare}
                          Color={
                            item.fairValueShare > 1
                              ? "#0BB783"
                              : item.fairValueShare === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                  <StyledTableCell>
                    <ColoredBox
                      number={item.growthIdx}
                      Color={
                        item.growthIdx > 1
                          ? "#0BB783"
                          : item.growthIdx === 1
                          ? "#0095FF"
                          : "#B22234"
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <ColoredBox
                      number={item.priceIdx}
                      Color={
                        item.priceIdx > 1
                          ? "#0BB783"
                          : item.priceIdx === 1
                          ? "#0095FF"
                          : "#B22234"
                      }
                    />
                  </StyledTableCell>

                  <StyledTableCell>
                    {idx === 0 ? (
                      <></>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          typeof updateSku === "function" && updateSku(item.name);
                        }}
                      >
                        View
                      </Button>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export default TopSkus;
